import { useLegacyCard } from '@/src/common/hooks/useCardInformation'
import { formatDateToShortString } from '@/src/common/utils/date'
import { useLegacyScheduledTransfers } from '@/src/experiences/card-activity/components/feed/_highnote/ScheduledTransfers/useLegacyScheduledTransfers'
import type {
  MakePaymentComponentType,
  PaymentCardStackScreenProps,
} from '@/src/experiences/card-payments/screens/MakePayment'
import { useLegacyAutoPayEntryPoint } from '@/src/experiences/card-payments/components/useAutoPayEntryPoint/useLegacyAutoPayEntryPoint'
import { Accordion } from '@/src/design-system/accordion'
import {
  ScheduledPaymentEntryPoint,
  styles as scheduledPaymentEntryPointStyles,
} from '@/src/experiences/card-payments/components/ScheduledPaymentEntryPoint'
import { PaymentIcon } from '@/src/assets/icons/PaymentIcon'
import { Text } from 'react-native'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import { formatCurrency } from '@/src/common/utils/general'
import { formatOneTimePaymentDate } from '@/src/app/main/(tabs)/card/card-management/payment/oneTimePayment/[id]'
import { LegacyScheduledTransferRow } from '@/src/experiences/card-activity/components/feed/_highnote/ScheduledTransfers/LegacyScheduledTransferRow'

export function LegacyMakePayment({
  MakePaymentComponent,
  ...props
}: {
  MakePaymentComponent: MakePaymentComponentType
} & PaymentCardStackScreenProps) {
  const autoPayEntryPoint = useLegacyAutoPayEntryPoint(props)

  const { data: scheduledTransfers, loading: loadingScheduledTransfers } =
    useLegacyScheduledTransfers()
  const oneTimeScheduledTransfers = loadingScheduledTransfers
    ? 'loading'
    : scheduledTransfers?.filter((s) => s.__typename === 'OneTimeAchTransferResponse') || []

  const cardInformation = useLegacyCard().data?.getCreditCardInformation.node
  const statementDueOnValue = cardInformation?.financialAccount.statementPaymentDueOn?.toString()
  const statementDueOn = statementDueOnValue
    ? formatDateToShortString(new Date(statementDueOnValue))
    : undefined

  return autoPayEntryPoint === 'loading' || oneTimeScheduledTransfers === 'loading' ? (
    <MakePaymentComponent loading {...props} />
  ) : (
    <MakePaymentComponent
      statementDueOn={statementDueOn}
      autoPayEntryPoint={autoPayEntryPoint}
      oneTimeScheduledTransfers={
        oneTimeScheduledTransfers[0] ? (
          oneTimeScheduledTransfers[1] ? (
            <Accordion
              triggerProps={{
                style: { paddingLeft: 16, paddingRight: 16, paddingTop: 16, paddingBottom: 16 },
              }}
              trigger={
                <ScheduledPaymentEntryPoint
                  Icon={PaymentIcon}
                  chevronWrapperProps={{ display: 'none' }}
                >
                  <Text
                    style={scheduledPaymentEntryPointStyles.title}
                    testID={CardPaymentsConstants.Ids.Text.MultipleOneTimePaymentsScheduled}
                  >
                    Multiple One-time Payments scheduled
                  </Text>
                </ScheduledPaymentEntryPoint>
              }
            >
              {oneTimeScheduledTransfers.map((oneTimeScheduledTransfer, index) => (
                <LegacyScheduledTransferRow
                  key={oneTimeScheduledTransfer.id}
                  {...oneTimeScheduledTransfer}
                  index={index}
                  linkProps={{
                    As: props.CardStackLink,
                    href: {
                      pathname: '/main/card/card-management/payment/oneTimePayment/[id]',
                      params: { id: oneTimeScheduledTransfer.id },
                    },
                  }}
                />
              ))}
            </Accordion>
          ) : (
            <props.CardStackLink
              href={{
                pathname: '/main/card/card-management/payment/oneTimePayment/[id]',
                params: { id: oneTimeScheduledTransfers[0].id },
              }}
              asChild
            >
              <ScheduledPaymentEntryPoint
                testID={CardPaymentsConstants.Ids.Text.SingleOneTimePaymentScheduled}
                Icon={PaymentIcon}
                style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 16, paddingBottom: 16 }}
              >
                <Text style={scheduledPaymentEntryPointStyles.title}>
                  One-time Payment scheduled for{' '}
                  {formatCurrency(oneTimeScheduledTransfers[0].transferAmount.text, {
                    isFloat: true,
                    minimumFractionDigits: 2,
                    hideCurrencySymbol: false,
                    decimals: null,
                  })}
                  {oneTimeScheduledTransfers[0].incomingScheduledTransferDate
                    ? ` on ${formatOneTimePaymentDate(oneTimeScheduledTransfers[0].incomingScheduledTransferDate)}`
                    : ''}
                </Text>
              </ScheduledPaymentEntryPoint>
            </props.CardStackLink>
          )
        ) : null
      }
      {...props}
    />
  )
}
